import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  FOLLOWUP_STAGES,
  type FollowupPipelineEntryResponse,
  type FollowupStage,
} from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { followupsApi } from '@/services/followups.api';
import { EmptyState, Spinner } from '@/components/ui';

/**
 * The integration pipeline: every person with at least one followup entry,
 * grouped by the stage their latest one left them at. A column, not a list —
 * the point of a pipeline is seeing how many people sit where at a glance.
 */
export function FollowupPipelinePage() {
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ['followups', 'pipeline'],
    queryFn: ({ signal }) => followupsApi.listPipeline(signal),
  });

  const entries = query.data ?? [];
  const byStage = new Map<FollowupStage, FollowupPipelineEntryResponse[]>();
  for (const stage of FOLLOWUP_STAGES) byStage.set(stage, []);
  for (const entry of entries) byStage.get(entry.stage)?.push(entry);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('people.followup.pipelineTitle')}</h1>
        </div>
      </header>

      {query.isPending && <Spinner label={t('common.states.loading')} />}
      {query.isSuccess && entries.length === 0 && (
        <EmptyState title={t('people.followup.pipelineEmpty')} />
      )}

      {entries.length > 0 && (
        <div className="pipeline-board">
          {FOLLOWUP_STAGES.map((stage) => {
            const column = byStage.get(stage) ?? [];
            return (
              <div className="pipeline-column" key={stage}>
                <div className="pipeline-column-header">
                  <span>{t(`people.followup.stage.${stage}`)}</span>
                  <span className="pipeline-column-count">{column.length}</span>
                </div>
                <ul className="pipeline-column-list">
                  {column.map((entry) => (
                    <li key={entry.personId}>
                      <Link to={`/people/${entry.personId}`} className="pipeline-card">
                        {entry.personDisplayName}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
